import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import api from '../../api/axios';
import { useToast } from '../../context/ToastContext';
import { StatusBadge, PriorityBadge } from '../../components/StatusBadge';

const ResolveTicket = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [statusComment, setStatusComment] = useState('');
  const [resolution, setResolution] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchTicket = () => {
    setLoading(true);
    api.get('/api/support/tickets')
      .then(res => {
        const t = res.data.find(t => t.ticketId === id);
        if (t) { setTicket(t); setStatus(t.status); }
        else toast.error(`Ticket ${id} not found`);
      })
      .catch(() => toast.error('Failed to load ticket details'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchTicket(); }, [id]); // eslint-disable-line

  const handleStatusUpdate = async () => {
    if (!statusComment.trim()) {
      toast.error('A comment is required when changing the status');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/api/support/tickets/${id}/status`, {
        status,
        comment: statusComment,
      });
      setTicket(res.data);
      setStatusComment('');
      toast.success(`Status updated to ${status}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleResolve = async () => {
    if (!resolution.trim()) {
      toast.error('Please enter a resolution note before resolving');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/api/support/tickets/${id}/resolve`, { resolution, status: 'RESOLVED' });
      toast.success('Ticket resolved successfully!');
      setTimeout(() => navigate('/support/tickets'), 1500);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resolve ticket');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="text-center py-20 text-gray-400">Loading...</div>
    </div>
  );

  if (!ticket) return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="text-center py-20 text-gray-400">Ticket not found</div>
    </div>
  );

  const isImage = ticket.attachmentType === 'IMAGE';

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/support/tickets')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-4">← Back</button>

        {/* Ticket Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-800">{ticket.ticketId}</h1>
              <p className="text-sm text-gray-500">{ticket.enterpriseName} &bull; KAM: {ticket.kamName}</p>
            </div>
            <div className="flex gap-2">
              <PriorityBadge priority={ticket.priority} />
              <StatusBadge status={ticket.status} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
            <div><span className="text-gray-400">Type:</span> <span className="font-medium ml-1">{ticket.ticketType || 'N/A'}</span></div>
            <div><span className="text-gray-400">Agent:</span> <span className="font-medium ml-1">{ticket.assignedAgentName || 'Unassigned'}</span></div>
            <div><span className="text-gray-400">Created:</span> <span className="font-medium ml-1">{ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : 'N/A'}</span></div>
            {ticket.updatedAt && (
              <div><span className="text-gray-400">Updated:</span> <span className="font-medium ml-1">{new Date(ticket.updatedAt).toLocaleString()}</span></div>
            )}
          </div>

          <div className="mb-4">
            <p className="text-sm font-medium text-gray-600 mb-1">Description</p>
            <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{ticket.description}</p>
          </div>

          {ticket.aiReason && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
              <p className="text-xs text-blue-700"><span className="font-semibold">AI Note:</span> {ticket.aiReason}</p>
            </div>
          )}

          {/* Attachment — image inline, PDF as link */}
          {ticket.attachmentUrl && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-600 mb-2">Attachment</p>
              {isImage ? (
                <div className="border border-gray-200 rounded-xl overflow-hidden inline-block max-w-full">
                  <img
                    src={`http://localhost:8081${ticket.attachmentUrl}`}
                    alt="Ticket attachment"
                    className="max-w-full max-h-80 object-contain"
                    onError={() => toast.error('Could not load attachment image')}
                  />
                </div>
              ) : (
                <a href={`http://localhost:8081${ticket.attachmentUrl}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary-dark bg-blue-50 px-4 py-2 rounded-lg">
                  📄 View PDF Attachment
                </a>
              )}
            </div>
          )}
        </div>

        {/* Update Actions */}
        {ticket.status !== 'RESOLVED' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
            <h2 className="font-semibold text-gray-700 mb-5">Update Ticket</h2>

            {/* Status + Comment */}
            <div className="border border-gray-200 rounded-xl p-4 mb-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Change Status</h3>
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">New Status</label>
                <select value={status} onChange={e => setStatus(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="OPEN">Open</option>
                  <option value="IN_PROGRESS">In Progress</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Comment <span className="text-red-500">*</span>
                  <span className="text-gray-400 font-normal ml-1">(required — explain the status change)</span>
                </label>
                <textarea
                  value={statusComment}
                  onChange={e => setStatusComment(e.target.value)}
                  rows={3}
                  placeholder="e.g. Investigating the card transaction issue with the payments team..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
              <button onClick={handleStatusUpdate} disabled={saving}
                className="w-full bg-blue-600 text-white font-medium py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm">
                {saving ? 'Updating...' : 'Update Status'}
              </button>
            </div>

            {/* Resolve */}
            <div className="border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Resolve Ticket</h3>
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Resolution Note <span className="text-red-500">*</span>
                </label>
                <textarea value={resolution} onChange={e => setResolution(e.target.value)}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  placeholder="Describe exactly how the issue was resolved..." />
              </div>
              <button onClick={handleResolve} disabled={saving}
                className="w-full bg-green-600 text-white font-semibold py-2.5 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors">
                {saving ? 'Resolving...' : '✓ Mark as Resolved'}
              </button>
            </div>
          </div>
        )}

        {/* Resolved banner */}
        {ticket.status === 'RESOLVED' && ticket.resolution && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
            <p className="text-sm font-semibold text-green-700 mb-1">✓ Resolved</p>
            <p className="text-sm text-green-600">{ticket.resolution}</p>
          </div>
        )}

        {/* Timeline */}
        {ticket.history && ticket.history.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-700 mb-5">Timeline</h2>
            <div className="relative">
              <div className="absolute left-1.5 top-2 bottom-2 w-0.5 bg-gray-200"></div>
              <div className="space-y-5">
                {ticket.history.map((h, i) => (
                  <div key={i} className="flex gap-4 relative">
                    <div className="flex-shrink-0 w-3.5 h-3.5 bg-primary rounded-full mt-0.5 border-2 border-white shadow-sm z-10"></div>
                    <div className="flex-1 pb-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-semibold text-gray-800">{h.action?.replace(/_/g, ' ')}</p>
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{h.doneByRole}</span>
                      </div>
                      <p className="text-sm text-gray-600">{h.note}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {h.doneBy} &bull; {h.createdAt ? new Date(h.createdAt).toLocaleString() : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResolveTicket;
